// src/pages/UserAdmin/AdminIntegracoes.js — chaves da API pública.
//
// Out/26. Tela onde o cliente gera a credencial que ClickUp, Bitrix24,
// Make, Zapier, n8n ou o sistema dele usam pra ler a base do CRM.
//
// A decisão de UI que manda no resto do arquivo: a chave aparece UMA vez,
// no retorno do POST, e nunca mais — o backend só guarda o hash. Tudo que
// está abaixo (o dialog dedicado, o aviso em vermelho, o botão de copiar
// grande, o "Já guardei" em vez de um "Fechar" neutro) existe pra que
// ninguém feche essa janela por reflexo e perca a chave. Quem perde não
// recupera: gera outra e refaz a configuração do outro lado.
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, List, ListItem, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, Divider, Chip, Stack, CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiKey, FiPlus, FiTrash2, FiCopy, FiCheck,
  FiAlertTriangle, FiZap,
} from 'react-icons/fi';
import AppSidebar from '../../components/AppSidebar';
import api from '../../services/Api';

const BRAND = '#25d366';

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return null;
  }
}

// Botão de copiar com confirmação visual. Sem o "Copiado!", o usuário
// clica duas ou três vezes sem saber se funcionou — e numa tela onde o
// valor só existe nesta sessão, essa dúvida custa caro.
function CopyButton({ value, label = 'Copiar', size = 'small' }) {
  const [done, setDone] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // clipboard API exige contexto seguro (https) e permissão. Em
      // http:// local ela rejeita — o fallback põe o valor selecionado
      // numa textarea pra o usuário copiar no teclado em vez de ficar
      // sem saída.
      const ta = document.createElement('textarea');
      ta.value = value;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* sem alternativa */ }
      document.body.removeChild(ta);
    }
    setDone(true);
    setTimeout(() => setDone(false), 2000);
  };

  return (
    <Button
      onClick={copy}
      size={size}
      startIcon={done ? <FiCheck size={14} /> : <FiCopy size={14} />}
      sx={{
        textTransform: 'none', fontWeight: 700, borderRadius: 2,
        color: done ? '#16a34a' : '#334155',
        bgcolor: done ? '#f0fdf4' : '#f1f5f9',
        '&:hover': { bgcolor: done ? '#dcfce7' : '#e2e8f0' },
      }}
    >
      {done ? 'Copiado' : label}
    </Button>
  );
}

export default function AdminIntegracoes() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [keys, setKeys] = useState([]);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  // Chave recém-criada em claro. Sai do estado quando o usuário confirma
  // que guardou — e some de vez, porque não há de onde buscar de novo.
  const [freshKey, setFreshKey] = useState(null);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, i] = await Promise.all([
        api.get('/integrations/keys'),
        api.get('/integrations/info'),
      ]);
      setKeys(Array.isArray(k.data) ? k.data : []);
      setInfo(i.data || null);
    } catch (err) {
      setMsg({
        type: 'error',
        text: err.response?.data?.detail || 'Não foi possível carregar as integrações.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await api.post('/integrations/keys', { name: newName.trim() });
      setFreshKey(res.data);
      setCreateOpen(false);
      setNewName('');
      load();
    } catch (err) {
      setMsg({
        type: 'error',
        text: err.response?.data?.detail || 'Não foi possível criar a chave.',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key) => {
    setRevoking(null);
    try {
      await api.delete(`/integrations/keys/${key.id}`);
      setMsg({ type: 'success', text: `Chave "${key.name}" revogada. O acesso parou agora.` });
      load();
    } catch (err) {
      setMsg({
        type: 'error',
        text: err.response?.data?.detail || 'Não foi possível revogar a chave.',
      });
    }
  };

  const curl = info
    ? `curl -H "X-API-Key: SUA_CHAVE" \\\n  "${info.base_url}/contacts?per_page=50"`
    : '';

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <AppSidebar user={user} />

      <Box sx={{
        flexGrow: 1,
        ml: { xs: 0, md: 'var(--sidebar-width, 260px)' },
        transition: 'margin-left 0.25s ease',
        p: { xs: 2, md: 4 },
        maxWidth: 900,
      }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={4}>
          <Button
            startIcon={<FiArrowLeft />}
            onClick={() => navigate('/dashboard')}
            sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}
          >
            Voltar
          </Button>
          <Divider orientation="vertical" flexItem />
          <FiKey size={22} color={BRAND} />
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0f172a">
              Integrações
            </Typography>
            <Typography variant="body2" color="#64748b">
              Conecte a Cetto ao ClickUp, Bitrix24, Make, Zapier ou ao seu próprio sistema
            </Typography>
          </Box>
        </Stack>

        {msg.text && (
          <Alert
            severity={msg.type || 'info'}
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setMsg({ type: '', text: '' })}
          >
            {msg.text}
          </Alert>
        )}

        {/* ── Como usar ─────────────────────────────────────────────── */}
        <Paper elevation={0} sx={{
          borderRadius: 3, border: '1px solid #e2e8f0', mb: 3, overflow: 'hidden',
        }}>
          <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <FiZap size={16} color={BRAND} />
              <Typography variant="h6" fontWeight={700} color="#0f172a">
                Como conectar
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="#475569" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              Crie uma chave abaixo e informe ela na outra plataforma. A Cetto
              devolve seus contatos em JSON — use <strong>updated_since</strong> para
              trazer só o que mudou desde a última sincronização, em vez de
              baixar a base inteira toda vez.
            </Typography>

            {info && (
              <Stack spacing={1.5}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="#94a3b8" fontWeight={700}>
                      ENDEREÇO DA API
                    </Typography>
                    <Typography sx={{
                      fontFamily: 'monospace', fontSize: 13, color: '#0f172a',
                      wordBreak: 'break-all',
                    }}>
                      {info.base_url}
                    </Typography>
                  </Box>
                  <CopyButton value={info.base_url} />
                </Stack>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="#94a3b8" fontWeight={700}>
                      CABEÇALHO DE AUTENTICAÇÃO
                    </Typography>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: 13, color: '#0f172a' }}>
                      {info.auth_header}: sua chave
                    </Typography>
                  </Box>
                  <CopyButton value={info.auth_header} />
                </Stack>

                <Box sx={{
                  p: 2, borderRadius: 2, bgcolor: '#0f172a',
                  position: 'relative', overflowX: 'auto',
                }}>
                  <Typography component="pre" sx={{
                    fontFamily: 'monospace', fontSize: 12.5, color: '#e2e8f0',
                    m: 0, whiteSpace: 'pre',
                  }}>
                    {curl}
                  </Typography>
                </Box>

                <Typography variant="caption" color="#64748b">
                  Seu plano {info.plan} permite {info.rate_limit_per_minute} requisições
                  por minuto por chave.
                </Typography>
              </Stack>
            )}
          </Box>
        </Paper>

        {/* ── Chaves ────────────────────────────────────────────────── */}
        <Paper elevation={0} sx={{
          borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden',
        }}>
          <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #f1f5f9' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
              <Typography variant="h6" fontWeight={700} color="#0f172a">
                Suas chaves
              </Typography>
              <Button
                onClick={() => setCreateOpen(true)}
                startIcon={<FiPlus size={14} />}
                variant="contained"
                disabled={Boolean(info) && keys.length >= info.max_active_keys}
                sx={{
                  bgcolor: BRAND, color: 'white', fontWeight: 700,
                  textTransform: 'none', borderRadius: 2, boxShadow: 'none',
                  '&:hover': { bgcolor: '#1ea855', boxShadow: 'none' },
                }}
              >
                Criar chave
              </Button>
            </Stack>
          </Box>

          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={22} sx={{ color: BRAND }} />
            </Box>
          ) : keys.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <FiKey size={36} color="#cbd5e1" />
              <Typography variant="h6" color="#94a3b8" mt={2} fontWeight={600}>
                Nenhuma chave ainda
              </Typography>
              <Typography variant="body2" color="#cbd5e1" mt={0.5}>
                Crie a primeira para conectar a Cetto a outra plataforma.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {keys.map((k, i) => (
                <Box key={k.id}>
                  {i > 0 && <Divider />}
                  <ListItem
                    sx={{ px: 3, py: 2 }}
                    secondaryAction={
                      <Tooltip title="Revogar" arrow>
                        <IconButton
                          onClick={() => setRevoking(k)}
                          sx={{ color: '#94a3b8', '&:hover': { color: '#dc2626', bgcolor: '#fef2f2' } }}
                        >
                          <FiTrash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography variant="body1" fontWeight={700} color="#0f172a">
                            {k.name}
                          </Typography>
                          <Chip
                            label={`${k.key_prefix}…`}
                            size="small"
                            sx={{
                              fontFamily: 'monospace', fontSize: '0.7rem', height: 22,
                              bgcolor: '#f1f5f9', color: '#475569',
                              border: '1px solid #e2e8f0', borderRadius: '6px',
                            }}
                          />
                        </Stack>
                      }
                      secondary={
                        <Typography variant="caption" color="#64748b">
                          {k.last_used_at
                            ? `Usada pela última vez em ${fmtDate(k.last_used_at)}`
                            : 'Nunca usada'}
                          {k.created_by_name ? ` · criada por ${k.created_by_name}` : ''}
                          {fmtDate(k.created_at) ? ` em ${fmtDate(k.created_at)}` : ''}
                        </Typography>
                      }
                    />
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
        </Paper>
      </Box>

      {/* ── Criar ───────────────────────────────────────────────────── */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>Criar chave</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#64748b" sx={{ mb: 2 }}>
            Dê um nome que lembre onde ela vai ser usada. Isso é o que permite
            revogar a certa depois.
          </Typography>
          <TextField
            autoFocus fullWidth
            label="Nome"
            placeholder="ClickUp — produção"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            inputProps={{ maxLength: 80 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
            variant="contained"
            startIcon={creating ? <CircularProgress size={12} color="inherit" /> : null}
            sx={{
              bgcolor: BRAND, color: 'white', fontWeight: 700, textTransform: 'none',
              borderRadius: 2, boxShadow: 'none',
              '&:hover': { bgcolor: '#1ea855', boxShadow: 'none' },
            }}
          >
            {creating ? 'Criando…' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Chave criada (aparece UMA vez) ──────────────────────────── */}
      <Dialog
        open={Boolean(freshKey)}
        // Sem onClose: clicar fora ou apertar Esc não fecha. É o único
        // dialog do sistema assim, e de propósito — fechar sem copiar
        // perde a chave pra sempre.
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a', pb: 1 }}>
          Chave criada
        </DialogTitle>
        <DialogContent>
          <Alert
            severity="warning"
            icon={<FiAlertTriangle size={18} />}
            sx={{ borderRadius: 2, mb: 2.5 }}
          >
            Copie agora. Esta é a única vez que a chave aparece — depois de
            fechar, nem nós conseguimos mostrá-la de novo.
          </Alert>

          <Typography variant="caption" color="#94a3b8" fontWeight={700}>
            {freshKey?.name?.toUpperCase()}
          </Typography>

          <Box sx={{
            mt: 0.5, p: 2, borderRadius: 2, bgcolor: '#0f172a',
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Typography sx={{
              fontFamily: 'monospace', fontSize: 13, color: '#4ade80',
              wordBreak: 'break-all', flexGrow: 1,
            }}>
              {freshKey?.key}
            </Typography>
          </Box>

          <Box sx={{ mt: 2 }}>
            <CopyButton value={freshKey?.key || ''} label="Copiar chave" size="medium" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setFreshKey(null)}
            variant="contained"
            sx={{
              bgcolor: '#0f172a', color: 'white', fontWeight: 700,
              textTransform: 'none', borderRadius: 2, boxShadow: 'none',
              '&:hover': { bgcolor: '#1e293b', boxShadow: 'none' },
            }}
          >
            Já guardei a chave
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Revogar ─────────────────────────────────────────────────── */}
      <Dialog
        open={Boolean(revoking)}
        onClose={() => setRevoking(null)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>
          Revogar "{revoking?.name}"?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#475569" sx={{ lineHeight: 1.7 }}>
            A integração que usa esta chave para de funcionar imediatamente.
            Não dá para reativar — se precisar de novo, crie outra chave e
            atualize a configuração do outro lado.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevoking(null)} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancelar
          </Button>
          <Button
            onClick={() => handleRevoke(revoking)}
            variant="contained"
            sx={{
              bgcolor: '#dc2626', color: 'white', fontWeight: 700,
              textTransform: 'none', borderRadius: 2, boxShadow: 'none',
              '&:hover': { bgcolor: '#b91c1c', boxShadow: 'none' },
            }}
          >
            Revogar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
